/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Col, PageHeader, Row, Typography } from 'antd';
import { Header } from 'components/header';
import {
  AddressCardIcon,
  CalendarIcon,
  EmptyAvatarIcon,
  LinkedInLogoIcon,
  MailIcon,
  MapMarkerIcon,
  UsersIcon,
} from 'components/icons';
import { Link } from 'components/link';
import { List } from 'components/list/list';
import { ListItem } from 'components/list/list-item';
import { useImageBlobSrc } from 'hooks';
import { EpEmployee } from 'types/graphql-types';
import styles from './styles.less';

const { Text } = Typography;

type PersonalInfoViewProps = {
  employee: EpEmployee;
};

export const PersonalInfoView: FC<PersonalInfoViewProps> = ({ employee }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('translation');
  const {
    country,
    department,
    experienceInsideCompany,
    experienceOutsideCompany,
    firstName,
    lastName,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    lastUpdated,
    linkedIn,
    login,
    officeLocation,
    photo,
    qualification,
  } = employee;
  const fullName = `${firstName} ${lastName}`;
  const skillTextClass = styles['personal-info-text'];
  const lastUpdateString = (
    lastUpdated ? new Date(lastUpdated as string) : new Date('2020-01-01')
  ).toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const { src } = useImageBlobSrc(photo);

  return (
    <PageHeader ghost={false}>
      <Row>
        <Col
          span={24}
          lg={{ flex: '240px', order: 2, span: 6 }}
          className={styles['profile-img-container']}
        >
          <Avatar
            className={styles['profile-img']}
            src={src}
            alt="Profile"
            size={220}
          >
            <EmptyAvatarIcon />
          </Avatar>
        </Col>
        <Col span={24} lg={{ flex: 'auto', span: 18 }}>
          <Row className={styles['name-info']}>
            <Header className={styles['name-header']}>{fullName}</Header>
          </Row>
          <Row gutter={[0, 16]}>
            <Col span={24} sm={{ span: 12 }}>
              <List>
                <ListItem className={styles['mb-16']}>
                  <AddressCardIcon fill="#69AAFF" />
                  <Text type="secondary" className={skillTextClass}>
                    {qualification || '—'}
                  </Text>
                </ListItem>
                <ListItem className={styles['mb-16']}>
                  <MailIcon fill="#69AAFF" />
                  <Text type="secondary" className={skillTextClass}>
                    {`${login.toLowerCase()}@itransition.com`}
                  </Text>
                </ListItem>
                <ListItem>
                  <CalendarIcon fill="#69AAFF" />
                  <Text type="secondary" className={skillTextClass}>
                    {`${experienceInsideCompany}/${experienceOutsideCompany} years of experience in itransition`}
                  </Text>
                </ListItem>
              </List>
            </Col>
            <Col span={24} sm={{ offset: 1, span: 11 }}>
              <List>
                <ListItem className={styles['mb-16']}>
                  <UsersIcon fill="#69AAFF" />
                  <Text type="secondary" className={skillTextClass}>
                    {department || '—'}
                  </Text>
                </ListItem>
                <ListItem className={styles['mb-16']}>
                  <MapMarkerIcon fill="#69AAFF" />
                  <Text type="secondary" className={skillTextClass}>
                    {country || officeLocation || '—'}
                  </Text>
                </ListItem>
                <ListItem>
                  {linkedIn ? (
                    <Link to={linkedIn} name="LinkedIn" flex external>
                      <LinkedInLogoIcon fill="#69AAFF" />
                      <Text type="secondary" className={skillTextClass}>
                        LinkedIn
                      </Text>
                    </Link>
                  ) : (
                    <>
                      <LinkedInLogoIcon fill="#69AAFF" />
                      <Text type="secondary" className={skillTextClass}>
                        —
                      </Text>
                    </>
                  )}
                </ListItem>
              </List>
            </Col>
          </Row>
          <Row className={styles['share-footer']}>
            <Text className={styles['share-footer-text']}>
              {t('Last update')} {lastUpdateString}
            </Text>
          </Row>
        </Col>
      </Row>
    </PageHeader>
  );
};
